import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `affiliate` domain — the pages under `/affiliate`
 * (`pages/affiliate/affiliate-nav.ts`). `AffiliateLayout` mounts each page's
 * guide button from its nav id, so a nav page needs no `pageId` binding of its
 * own.
 *
 * This whole domain documents the PARTNER world (`commission.partners` and the
 * ladders hanging off it). It is not the organization tree: a partner row has
 * its own random id and its own `parentPartnerId` chain, and touches the tree
 * only through `agent_id`, which names the agent the partner is scoped to.
 * Nothing an operator changes here reaches per-node PT or the weekly node
 * settlement, and nothing there reaches these rates — the two settlement
 * engines never read each other's tables.
 *
 * Rates are stored as integer BASIS POINTS everywhere (0..10000, validated at
 * both the route and the repo). Percent exists only on screen. Partner-share
 * figures come from `engine/partner-share.ts`: `commission.partner_periods`
 * holds them per round, recomputed hourly for every period not yet closed, and
 * the revenue and monthly reports recompute them live for any range. Payments
 * (`commission.partner_payments`) only record transfers made off the platform;
 * nothing on these pages moves wallet money (ADR-0024).
 */
export const affiliateGuide: DomainGuide = {
  domain: 'affiliate',
  pages: [
    {
      id: 'affiliate.partners',
      route: '/affiliate',
      permission: 'affiliate:read',
      status: 'live',
      fields: [
        { key: 'upline', kind: 'text' },
        { key: 'code', kind: 'text' },
        { key: 'model', kind: 'status' },
        { key: 'username', kind: 'text' },
        { key: 'sharePercentBp', kind: 'percent', formula: 'stored_bp = percent × 100' },
        { key: 'bankAccountNo', kind: 'text' },
        { key: 'paymentPeriod', kind: 'text' },
        { key: 'createdAt', kind: 'date' },
        { key: 'lastLoginAt', kind: 'date' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 7,
      warningCount: 0,
    },
    {
      id: 'affiliate.groups',
      route: '/affiliate/groups, /affiliate/groups/:groupId/revenue',
      permission: 'affiliate:read',
      status: 'live',
      fields: [
        { key: 'name', kind: 'text' },
        { key: 'description', kind: 'text' },
        { key: 'status', kind: 'status' },
        { key: 'downlineEnabled', kind: 'status' },
        {
          key: 'subSharePercentBp',
          kind: 'percent',
          formula: 'downline = max(0, −(childBase + childExpenses) × downline%)',
        },
        { key: 'minMembers', kind: 'count' },
        { key: 'maxMembers', kind: 'count' },
        { key: 'minNewMembers', kind: 'count' },
        { key: 'maxNewMembers', kind: 'count' },
        { key: 'minFtdMembers', kind: 'count' },
        { key: 'minTurnover', kind: 'money-unsigned' },
        { key: 'maxTurnover', kind: 'money-unsigned' },
        { key: 'minLoss', kind: 'money-unsigned', formula: 'matched against |base|' },
        { key: 'maxLoss', kind: 'money-unsigned', formula: 'matched against |base|' },
        { key: 'shareType', kind: 'status' },
        {
          key: 'shareValueBp',
          kind: 'percent',
          formula: 'share = min(−base × share%, shareMaxCap) − expense',
        },
        { key: 'fixedValue', kind: 'money-unsigned', formula: 'share = base < 0 ? fixedValue : 0' },
        { key: 'shareMaxCap', kind: 'money-unsigned' },
        { key: 'expenseType', kind: 'status' },
        {
          key: 'expenseValueBp',
          kind: 'percent',
          formula: 'expense = min(expenses × (expense% ?? share%), expenseMax)',
        },
        { key: 'expenseAmount', kind: 'money-unsigned' },
        { key: 'expenseMax', kind: 'money-unsigned' },
      ],
      cautionCount: 7,
      warningCount: 0,
    },
    {
      id: 'affiliate.payments',
      route: '/affiliate/payments',
      permission: 'affiliate:read',
      status: 'live',
      fields: [
        { key: 'paymentPeriod', kind: 'text' },
        { key: 'state', kind: 'status' },
        { key: 'roundAmount', kind: 'money-signed' },
        { key: 'outstanding', kind: 'money-signed' },
        {
          key: 'net',
          kind: 'money-signed',
          formula: 'net = outstanding + amountThisRound while due or pending, else outstanding',
        },
        {
          key: 'outstandingAfter',
          kind: 'money-signed',
          formula: 'transfer: net − amount · receive: net + amount · carry: net',
        },
      ],
      cautionCount: 9,
      warningCount: 1,
    },
    {
      id: 'affiliate.paymentSummary',
      route: '/affiliate/payments/summary',
      permission: 'affiliate:read',
      status: 'live',
      fields: [
        { key: 'transfer', kind: 'money-unsigned', formula: 'Σ amount of transfer lines' },
        { key: 'receive', kind: 'money-unsigned', formula: 'Σ amount of receive lines' },
        {
          key: 'carry',
          kind: 'money-unsigned',
          formula: "Σ over partners |balanceAfter of the partner's newest line in the range|",
        },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      id: 'affiliate.revenue',
      route: '/affiliate/reports/revenue',
      permission: 'affiliate:read',
      status: 'live',
      fields: [
        { key: 'register', kind: 'count' },
        { key: 'ftd', kind: 'count' },
        { key: 'active', kind: 'count' },
        { key: 'winloss', kind: 'money-signed' },
        {
          key: 'afterDeduction',
          kind: 'money-signed',
          formula: 'afterDeduction = winloss − companyDeduction (general and boxing TFT rates)',
        },
        { key: 'payment', kind: 'money-unsigned', formula: 'depositFee = deposits × depositFee%' },
        { key: 'expenses', kind: 'money-unsigned' },
        { key: 'initialShare', kind: 'money-signed' },
        { key: 'shareFromPartners', kind: 'money-signed' },
        { key: 'netShare', kind: 'money-signed', formula: 'netShare = share + shareFromPartners' },
        {
          key: 'companyPl',
          kind: 'money-signed',
          formula: 'companyPl = −winloss − expenses − netShare',
        },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      id: 'affiliate.monthly',
      route: '/affiliate/reports/monthly',
      permission: 'affiliate:read',
      status: 'live',
      fields: [
        { key: 'affiliatesNew', kind: 'count' },
        { key: 'affiliatesInactive', kind: 'count' },
        { key: 'affiliatesActive', kind: 'count' },
        { key: 'winloss', kind: 'money-signed' },
        { key: 'netShare', kind: 'money-signed' },
        {
          key: 'companyPl',
          kind: 'money-signed',
          formula: 'companyPl = −winloss − expenses − netShare',
        },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      id: 'affiliate.settings',
      route: '/affiliate/settings',
      permission: 'affiliate:read',
      status: 'live',
      fields: [
        { key: 'brand', kind: 'text' },
        {
          key: 'defaultBp',
          kind: 'percent',
          formula:
            'base = W_general × (1 − general%) + W_boxing × (1 − boxing%) + deposits × depositFee%',
        },
        { key: 'boxingBp', kind: 'percent' },
        { key: 'depositFeeBp', kind: 'percent' },
        { key: 'boxingProvider', kind: 'text' },
      ],
      cautionCount: 6,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
